import React, { useMemo, useCallback, useState, useEffect } from 'react'
import {
    DndContext,
    DragStartEvent,
    DragEndEvent,
    DragOverlay,
    TouchSensor,
    MouseSensor,
    closestCenter,
    useSensor,
    useSensors,
    defaultDropAnimationSideEffects,
    MeasuringStrategy,
} from '@dnd-kit/core'
import {
    SortableContext,
    useSortable,
    rectSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Masonry } from 'masonic'
import { AddProductImagePlaceholder } from '@/components/products/placeholders/AddProductImagePlaceholder'
import { ProductImagePreviewCard } from './ProductImagePreviewCard'
import { classNames } from '@/utils/classNames'

export interface ImageItem {
    id: string
    url: string
}

interface AddButtonItem {
    id: 'add-button'
    type: 'add'
}

interface SortableImageItem extends ImageItem {
    type: 'image'
    index: number
}

type MasonryItem = AddButtonItem | SortableImageItem

export interface SortableImageGridProps {
    items: ImageItem[]
    uploadProgress: Record<string, number>
    isUploading: boolean
    hasExcessImages: boolean
    hasError: boolean
    onAddClick: () => void
    onRemove: (id: string) => void
    onDragStart: (e: DragStartEvent) => void
    onDragEnd: (e: DragEndEvent) => void
    onDragCancel: () => void
    activeImageId: string | null
    minImages?: number
    className?: string
}

const SortableImageCard: React.FC<{
    item: ImageItem
    index: number
    onRemove: () => void
    uploadProgress?: number
    minImages?: number
    totalItems: number
}> = ({ item, index, onRemove, uploadProgress, minImages, totalItems }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: item.id, data: { type: 'image', index } })
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 1000 : 1,
    }

    // Don't show progress overlay for initial scraped images that are already uploaded
    const isInitialImage = item.id.startsWith('initial-')
    const shouldShowProgress =
        !isInitialImage && uploadProgress !== undefined && uploadProgress < 100

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className={`${isDragging ? 'touch-none opacity-50 select-none' : ''}`}
        >
            <ProductImagePreviewCard
                image={item.url}
                className="w-full"
                index={String(index + 1)}
                showClose={minImages === undefined || totalItems > minImages}
                onClose={onRemove}
            />
            {shouldShowProgress && (
                <div className="bg-opacity-50 absolute inset-0 flex items-center justify-center rounded-2xl bg-black">
                    <div className="text-sm font-medium text-white">
                        {uploadProgress}%
                    </div>
                </div>
            )}
        </div>
    )
}

export default function SortableImageGrid({
    items,
    uploadProgress,
    isUploading,
    hasExcessImages,
    hasError,
    onAddClick,
    onRemove,
    onDragStart,
    onDragEnd,
    onDragCancel,
    activeImageId,
    minImages,
    className,
}: SortableImageGridProps) {
    // Responsive column count
    const [columnCount, setColumnCount] = useState(2)

    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 768px)')
        const handleChange = (e: MediaQueryListEvent) => {
            setColumnCount(e.matches ? 2 : 5)
        }

        // Set initial value
        setColumnCount(mediaQuery.matches ? 2 : 5)

        // Listen for changes
        mediaQuery.addEventListener('change', handleChange)
        return () => mediaQuery.removeEventListener('change', handleChange)
    }, [])

    const mouseSensor = useSensor(MouseSensor, {
        activationConstraint: { distance: 8 },
    })
    const touchSensor = useSensor(TouchSensor, {
        activationConstraint: { delay: 300, tolerance: 8 },
    })
    const sensors = useSensors(mouseSensor, touchSensor)
    const activeItem = activeImageId
        ? items.find((i) => i.id === activeImageId)
        : null

    // Memoize masonry items with a stable key that forces re-render when needed
    const masonryItems = useMemo(() => {
        const result: MasonryItem[] = [
            ...items.map((item, index) => ({
                ...item,
                type: 'image' as const,
                index,
            })),
            { id: 'add-button', type: 'add' },
        ]
        return result
    }, [items])

    // Simplified key generation - only re-render when the number of items changes significantly
    const masonryKey = useMemo(() => {
        return `masonry-${items.length}`
    }, [items.length])

    // Memoize the render function to prevent unnecessary re-renders
    const renderMasonryItem = useCallback(
        ({ data }: { data: MasonryItem }) => {
            if (data.type === 'add') {
                return (
                    <label className="cursor-pointer">
                        <AddProductImagePlaceholder
                            onClick={onAddClick}
                            type="image"
                            className={classNames(
                                'h-[14.688rem] w-full',
                                (hasError || hasExcessImages) &&
                                'border border-red-500 text-red-500'
                            )}
                            colorIcon={
                                hasError || hasExcessImages ? 'red' : undefined
                            }
                            disabled={items.length >= 10 || isUploading}
                        />
                    </label>
                )
            }

            // TypeScript now knows this is a SortableImageItem
            return (
                <SortableImageCard
                    key={data.id}
                    item={data}
                    index={data.index}
                    onRemove={() => onRemove(data.id)}
                    uploadProgress={uploadProgress[data.url]}
                    minImages={minImages}
                    totalItems={items.length}
                />
            )
        },
        [
            onAddClick,
            hasError,
            hasExcessImages,
            items.length,
            isUploading,
            onRemove,
            uploadProgress,
            minImages,
        ]
    )

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onDragCancel={onDragCancel}
            measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
        >
            <SortableContext
                items={items.map((i) => i.id)}
                strategy={rectSortingStrategy}
            >
                <section className={`${className || ''} w-full`}>
                    {/* Add a stable container with explicit width constraints */}
                    <div className="w-full max-w-full overflow-hidden">
                        <Masonry
                            key={masonryKey}
                            items={masonryItems}
                            columnCount={columnCount}
                            columnGutter={10}
                            rowGutter={8}
                            render={renderMasonryItem}
                        />
                    </div>
                </section>
            </SortableContext>
            <DragOverlay
                dropAnimation={{
                    duration: 300,
                    easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
                    sideEffects: defaultDropAnimationSideEffects({
                        styles: { active: { opacity: '0.5' } },
                    }),
                }}
            >
                {activeItem && (
                    <div className="scale-105 rotate-6 transform">
                        <ProductImagePreviewCard
                            image={activeItem.url}
                            className="w-full max-w-[10.938rem]"
                            index={String(
                                items.findIndex((i) => i.id === activeImageId) +
                                1
                            )}
                            showClose={false}
                        />
                    </div>
                )}
            </DragOverlay>
        </DndContext>
    )
}
